"use client";

import * as React from "react";
import { lazy, Suspense, useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useIdle } from "@/hooks/useIdle";
import { Button } from "@/components/ui/button";

// Perf: o bloco de conta é o único que depende de auth (SDK do backend).
// Carregado sob demanda para manter o bundle inicial da LP mínimo.
const NavAccount = lazy(() =>
  import("@/components/ui/navbar-account").then((m) => ({ default: m.NavAccount })),
);

type NavItem = { label: string; href: string };

const DEFAULT_ITEMS: NavItem[] = [];


export function Navbar1({
  items = DEFAULT_ITEMS,
  ctaLabel = "Entrar",
  ctaHref = "/auth",
  mobileOnlyItems = false,
  mobileAccount = false,
  forceDark = false,
  darkAtTop = false,
  collapseOnScroll = false,
  shrinkOnScroll = false,
  hideOnFooter = false,
}: {

  items?: NavItem[];
  ctaLabel?: string;
  ctaHref?: string;
  mobileOnlyItems?: boolean;
  /** On mobile, show the account avatar / "Entrar" button instead of the hamburger menu. */
  mobileAccount?: boolean;
  forceDark?: boolean;
  /** Cápsula preta no topo da página; vira branca a partir de 24px de rolagem. */
  darkAtTop?: boolean;
  /** Recolhe a cápsula em um botão circular durante a rolagem para baixo. */
  collapseOnScroll?: boolean;
  /** Encurta a cápsula (mais estreita e compacta) assim que a página rola. */
  shrinkOnScroll?: boolean;
  /** Some com a cápsula no celular quando o rodapé entra na tela. */
  hideOnFooter?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [footerInView, setFooterInView] = useState(false);
  const accountReady = useIdle(1500);
  const lastScrollY = React.useRef(0);
  const scrollPositionOnCollapse = React.useRef(0);

  useEffect(() => {
    // Perf: a leitura do scroll roda no máximo uma vez por quadro e só
    // atualiza o estado quando o valor muda — evita re-render a cada pixel.
    let raf = 0;
    const update = () => {
      raf = 0;
      const latest = window.scrollY;
      const previous = lastScrollY.current;
      const next = latest > 24;
      setScrolled((prev) => (prev === next ? prev : next));

      if (collapseOnScroll) {
        if (isExpanded && latest > previous && latest > 150) {
          setIsExpanded(false);
          setIsOpen(false);
          scrollPositionOnCollapse.current = latest;
        } else if (
          !isExpanded &&
          latest < previous &&
          scrollPositionOnCollapse.current - latest > 80
        ) {
          setIsExpanded(true);
        }
      }

      lastScrollY.current = latest;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });

    // Rodapé visível: observado sem medir o layout a cada rolagem.
    let io: IntersectionObserver | undefined;
    let mo: MutationObserver | undefined;
    if (hideOnFooter && typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) setFooterInView(e.isIntersecting);
        },
        { rootMargin: "0px 0px -8px 0px" },
      );
      const attach = () => {
        const footer = document.querySelector("footer");
        if (footer) {
          io!.observe(footer);
          mo?.disconnect();
          return true;
        }
        return false;
      };
      if (!attach()) {
        mo = new MutationObserver(() => attach());
        mo.observe(document.body, { childList: true, subtree: true });
      }
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      io?.disconnect();
      mo?.disconnect();
    };
  }, [collapseOnScroll, isExpanded, hideOnFooter]);

  const toggleMenu = () => setIsOpen((v) => !v);

  const textColor = forceDark ? "text-black" : scrolled ? "text-black" : "text-white";
  const subtle = forceDark ? "text-black/70" : scrolled ? "text-black/70" : "text-white/70";
  const hover = forceDark ? "hover:text-black" : scrolled ? "hover:text-black" : "hover:text-white";

  const ctaFallback = (
    <Link
      to={ctaHref}
      className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 text-[14px] font-medium text-black transition-transform hover:scale-105"
    >
      {ctaLabel}
    </Link>
  );



  return (
    <div
      className={`pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-4 transition-opacity duration-300 lg:pt-6 ${
        footerInView && !isOpen ? "invisible opacity-0 md:visible md:opacity-100" : "opacity-100"
      }`}
    >
      <nav
        aria-label="Navegação principal"
        className={`pointer-events-auto relative flex h-12 items-center overflow-hidden rounded-full border transition-[max-width,width,background-color,border-color,box-shadow] duration-500 ease-[cubic-bezier(.22,1,.36,1)] backdrop-blur-xl ${
          collapseOnScroll && !isExpanded
            ? "w-12 cursor-pointer justify-center px-0 shadow-lg"
            : shrinkOnScroll && scrolled
              ? "w-full max-w-[320px] justify-between px-3"
              : "w-full max-w-[720px] justify-between px-3 lg:px-4"
        } ${
          forceDark
            ? "border-black/10 bg-[#F4F4F4]/80 shadow-[0_10px_40px_-12px_rgba(0,0,0,0.15)] md:bg-[#F4F4F4]/70"
            : scrolled
              ? "border-black/10 bg-white shadow-[0_10px_40px_-12px_rgba(0,0,0,0.25)] md:bg-white/90"
              : darkAtTop
                ? "border-white/10 bg-black/85 shadow-[0_10px_40px_-12px_rgba(0,0,0,0.45)]"
                : "border-white/15 bg-black/40 md:bg-white/[0.06]"
        }`}


      >
        <Link
          to="/"
          aria-label="Viny Club home"
          className={`flex shrink-0 items-center gap-2 transition-[opacity,transform] duration-300 ${
            collapseOnScroll && !isExpanded
              ? "pointer-events-none -translate-x-6 -rotate-180 opacity-0"
              : "translate-x-0 rotate-0 opacity-100 delay-100"
          }`}
        >
          <Logo withLink={false} variant={forceDark || scrolled ? "dark" : "light"} size={28} />
        </Link>


        <div
          className={`hidden items-center overflow-hidden text-[14px] font-medium transition-[opacity,transform,max-width] duration-300 md:flex ${subtle} ${
            collapseOnScroll && !isExpanded
              ? "pointer-events-none -translate-x-4 opacity-0"
              : shrinkOnScroll && scrolled
                ? "pointer-events-none max-w-0 -translate-x-3 opacity-0"
                : "max-w-[420px] translate-x-0 opacity-100 delay-100"
          } ${shrinkOnScroll && scrolled ? "gap-0" : "gap-7"}`}
        >
          {(mobileOnlyItems ? [] : items).map((item) => (
            <a key={item.label} href={item.href} className={`transition-colors ${hover}`}>
              {item.label}
            </a>
          ))}
        </div>

        <div
          className={`hidden transition-[opacity,transform] duration-300 md:block ${
            collapseOnScroll && !isExpanded
              ? "pointer-events-none translate-x-5 opacity-0"
              : "translate-x-0 opacity-100 delay-100"
          }`}
        >
          {accountReady ? (
            <Suspense fallback={ctaFallback}>
              <NavAccount
                variant="desktop"
                forceDark={forceDark}
                scrolled={scrolled}
                ctaHref={ctaHref}
                ctaLabel={ctaLabel}
              />
            </Suspense>
          ) : (
            ctaFallback
          )}
        </div>

        {mobileAccount ? (
          <div
            className={`transition-[opacity,transform] duration-300 md:hidden ${
              collapseOnScroll && !isExpanded
                ? "pointer-events-none translate-x-5 opacity-0"
                : "translate-x-0 opacity-100 delay-100"
            }`}
          >
            {accountReady ? (
              <Suspense
                fallback={
                  <Link
                    to={ctaHref}
                    className="inline-flex items-center rounded-full bg-white px-4 py-2 text-[13px] font-semibold text-black shadow-lg shadow-black/20"
                  >
                    Entrar
                  </Link>
                }
              >
                <NavAccount
                  variant="mobile"
                  forceDark={forceDark}
                  scrolled={scrolled}
                  ctaHref={ctaHref}
                  ctaLabel={ctaLabel}
                />
              </Suspense>
            ) : (
              <Link
                to={ctaHref}
                className="inline-flex items-center rounded-full bg-white px-4 py-2 text-[13px] font-semibold text-black shadow-lg shadow-black/20"
              >
                Entrar
              </Link>
            )}
          </div>
        ) : (

          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMenu}
            className={`rounded-full md:hidden ${textColor} ${
              collapseOnScroll && !isExpanded ? "pointer-events-none opacity-0" : "opacity-100"
            }`}
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}

        {collapseOnScroll ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setIsExpanded(true)}
            aria-label="Expandir menu"
            className={`absolute inset-0 h-12 w-12 rounded-full p-0 transition-[opacity,transform] duration-300 ${textColor} ${
              isExpanded
                ? "pointer-events-none scale-75 opacity-0"
                : "pointer-events-auto scale-100 opacity-100 delay-150"
            }`}
          >
            <Menu className="h-5 w-5" />
          </Button>
        ) : null}
      </nav>

      {isOpen && (
        <div className="pointer-events-auto fixed inset-0 z-50 flex flex-col bg-white p-6 animate-in fade-in duration-200 md:hidden">
            <div className="flex items-center justify-between">
              <Logo withLink={false} variant="dark" size={32} />
              <button type="button" onClick={toggleMenu} aria-label="Close menu" className="text-black">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-12 flex flex-col gap-6">
              {items.map((item, i) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => {
                    if (item.href.startsWith("#")) {
                      e.preventDefault();
                      const id = item.href.slice(1);
                      setIsOpen(false);
                      // Sections are lazy-mounted on scroll, so poll until the
                      // target exists and keep re-aligning while it grows.
                      const start = Date.now();
                      const tick = () => {
                        const el = document.getElementById(id);
                        if (el) {
                          el.scrollIntoView({ behavior: "smooth", block: "start" });
                          history.replaceState(null, "", item.href);
                          if (Date.now() - start < 1600) setTimeout(tick, 400);
                          return;
                        }
                        if (Date.now() - start < 4000) setTimeout(tick, 120);
                      };
                      setTimeout(tick, 300);
                    } else {
                      setIsOpen(false);
                    }
                  }}
                  style={{ animationDelay: `${0.05 * i}s` }}
                  className="animate-in fade-in slide-in-from-bottom-3 text-2xl font-medium tracking-tight text-black"
                >
                  {item.label}
                </a>
              ))}
              <Suspense
                fallback={
                  <Link
                    to={ctaHref}
                    onClick={() => setIsOpen(false)}
                    className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-black px-6 py-3 text-xs uppercase tracking-[0.18em] text-white"
                  >
                    {ctaLabel}
                  </Link>
                }
              >
                <NavAccount
                  variant="menu"
                  ctaHref={ctaHref}
                  ctaLabel={ctaLabel}
                  onNavigate={() => setIsOpen(false)}
                />
              </Suspense>

            </div>
        </div>
      )}
    </div>
  );
}

export default Navbar1;
